import { useState, useEffect, useRef, useCallback } from 'react';
import { Check, Lock, ArrowRight, AlertTriangle } from 'lucide-react';
import SivarSendWordmark from '../components/brand/SivarSendWordmark';

// ── Perfil Aduanero — SivarSend Desarrollos ─────────────────────────────────
// UI-only, mock data inline (no fetch calls). Two interaction models:
//   · Setup ("nuevo"): guided single-step form, one primary CTA.
//   · Edit (any other estado): per-field autosave, no global save button.
// The financial configuration is admin-set and opaque to the cliente — this
// screen only ever shows its activation chip, never numbers.

// ── Mock data ───────────────────────────────────────────────────────────────
const CLIENTE = {
  primer_nombre: 'María',
  full_name: 'María Alejandra Rivas', // last 4: 4587
};

const PERFIL_VACIO = {
  codigo_gestor: '',
  codigo_aduana: '',
  nombre_gestor: '',
  no_registro: '',
};

const PERFIL_LLENO = {
  codigo_gestor: 'GEST-2847',
  codigo_aduana: '10520',
  nombre_gestor: 'Roberto Antonio Menjívar',
  no_registro: 'SV-849302',
};

const ACTIVATED_AT = '8 de agosto de 2026';

// admin-set: shown only in revisión_requerida
const REVISION = {
  field: 'codigo_aduana',
  note: 'El código aduana no coincide con nuestra base. Revisá que sean los 5 dígitos correctos.',
};

// ── Field definitions + validation ──────────────────────────────────────────
const FIELDS = [
  {
    key: 'codigo_gestor',
    label: 'Código Gestor',
    example: 'Ej. GEST-2847',
    helper: 'Código de tu gestor aduanal ante la DGA.',
    maxLength: 12,
    autoUppercase: true,
  },
  {
    key: 'codigo_aduana',
    label: 'Código Aduana',
    example: 'Ej. 10520',
    helper: 'Número de la aduana donde opera tu gestor.',
    maxLength: 6,
    inputMode: 'numeric',
  },
  {
    key: 'nombre_gestor',
    label: 'Nombre Gestor',
    example: 'Ej. Roberto Antonio Menjívar',
    helper: 'Nombre completo de tu gestor aduanal, tal como aparece en su registro.',
    maxLength: 80,
  },
  {
    key: 'no_registro',
    label: 'No. Registro',
    example: 'Ej. SV-849302',
    helper: 'Número de registro del gestor ante la DGA.',
    maxLength: 14,
  },
];

// Returns { level: 'ok' | 'warn' | 'error' | null, message }.
// Amber (warn) = incomplete but recoverable. Red (error) = hard invalid only.
// Empty fields are neutral on blur — they only warn on submit attempt.
const validateField = (key, raw) => {
  const value = raw.trim();
  if (!value) return { level: null, message: '' };

  switch (key) {
    case 'codigo_gestor':
      if (!/^[A-Z0-9-]+$/i.test(value))
        return { level: 'error', message: 'Solo se permiten letras, números y guiones.' };
      if (value.length < 4)
        return { level: 'warn', message: 'El código debe tener entre 4 y 12 caracteres.' };
      return { level: 'ok', message: '' };

    case 'codigo_aduana':
      if (!/^\d+$/.test(value))
        return { level: 'error', message: 'El código aduana solo lleva números. Revisá que no tenga letras.' };
      if (value.length < 4)
        return { level: 'warn', message: 'Debe tener entre 4 y 6 dígitos.' };
      return { level: 'ok', message: '' };

    case 'nombre_gestor':
      if (value.length < 3)
        return { level: 'warn', message: 'Escribí el nombre completo de tu gestor (mínimo 3 caracteres).' };
      return { level: 'ok', message: '' };

    case 'no_registro':
      if (!/^[A-Z0-9-]+$/i.test(value))
        return { level: 'error', message: 'Solo se permiten letras, números y guiones.' };
      if (value.length < 6)
        return { level: 'error', message: 'El No. Registro debe tener al menos 6 caracteres.' };
      return { level: 'ok', message: '' };

    default:
      return { level: null, message: '' };
  }
};

// ── Mock persistence ────────────────────────────────────────────────────────
// Set window.__PERFIL_FALLA_RED = true in the console to simulate a dropped
// connection and watch the retry queue work.
const mockSave = () =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      if (typeof window !== 'undefined' && window.__PERFIL_FALLA_RED) reject(new Error('offline'));
      else resolve();
    }, 400);
  });

// ── Status chips ────────────────────────────────────────────────────────────
const CHIPS = {
  pendiente_de_activación: {
    label: 'Pendiente de activación',
    cls: 'bg-slate-100 text-slate-600 border-slate-200',
  },
  activado: {
    label: 'Activado',
    cls: 'bg-green-50 text-green-700 border-green-200',
  },
  revisión_requerida: {
    label: 'Revisión requerida',
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
  },
};

const EstadoChip = ({ estado }) => {
  const chip = CHIPS[estado];
  if (!chip) return null;
  return (
    <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded border shrink-0 ${chip.cls}`}>
      {chip.label}
    </span>
  );
};

// ── Financial configuration card (display-only, never numbers) ──────────────
const ConfigFinancieraCard = ({ estado }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5">
    <div className="flex items-center justify-between gap-3 mb-2">
      <div className="flex items-center gap-2">
        <Lock size={14} className="text-slate-400" />
        <p className="text-sm font-semibold text-slate-700">Configuración financiera</p>
      </div>
      <EstadoChip estado={estado === 'revisión_requerida' ? 'pendiente_de_activación' : estado} />
    </div>
    <p className="text-xs text-slate-500 leading-relaxed">
      Esta parte la configura SivarSend según tu operación. Vas a poder generar Anexos apenas la
      activemos.
    </p>
  </div>
);

// ── One field row (shared by setup and edit) ────────────────────────────────
const CampoPerfil = ({
  field,
  value,
  validation,
  onChange,
  onBlur,
  revisionNote,
  editControls, // null in setup; { dirty, status, savedSecondsAgo, onGuardar } in edit
}) => {
  const v = validation || { level: null, message: '' };
  const highlighted = Boolean(revisionNote);

  const borderCls = highlighted
    ? 'border-amber-400 focus:border-amber-500'
    : v.level === 'error'
    ? 'border-red-400 focus:border-red-500'
    : v.level === 'warn'
    ? 'border-amber-300 focus:border-amber-400'
    : 'border-slate-200 focus:border-slate-900';

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={field.key} className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {field.label}
      </label>
      <p className="text-xs text-slate-500 leading-snug">{field.helper}</p>
      <p className="text-xs text-slate-400">{field.example}</p>

      <div className="flex items-start gap-2 mt-1">
        <div className="relative flex-1">
          <input
            id={field.key}
            type="text"
            inputMode={field.inputMode || 'text'}
            maxLength={field.maxLength}
            autoComplete="off"
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            onBlur={() => onBlur(field.key)}
            className={`w-full h-11 px-3 pr-9 text-sm text-slate-900 bg-white border rounded-lg outline-none transition-colors ${borderCls}`}
          />
          {v.level === 'ok' && !highlighted && (
            <Check
              size={16}
              className="text-green-600 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
            />
          )}
        </div>

        {editControls && editControls.dirty && editControls.status !== 'guardando' && (
          <button
            type="button"
            onClick={() => editControls.onGuardar(field.key)}
            className="h-11 px-4 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-lg transition-colors shrink-0"
          >
            Guardar
          </button>
        )}
      </div>

      {highlighted && (
        <p className="text-xs text-amber-700 flex items-start gap-1.5 mt-0.5">
          <AlertTriangle size={13} className="shrink-0 mt-px" /> {revisionNote}
        </p>
      )}

      {!highlighted && v.level === 'warn' && (
        <p className="text-xs text-amber-600 flex items-start gap-1.5 mt-0.5">
          <AlertTriangle size={13} className="shrink-0 mt-px" /> {v.message}
        </p>
      )}
      {!highlighted && v.level === 'error' && (
        <p className="text-xs text-red-600 mt-0.5">{v.message}</p>
      )}

      {editControls && editControls.status === 'guardando' && (
        <p className="text-xs text-slate-400 mt-0.5">guardando…</p>
      )}
      {editControls && editControls.status === 'reintentando' && (
        <p className="text-xs text-amber-600 mt-0.5">
          Sin conexión — guardamos tu cambio y lo estamos reintentando…
        </p>
      )}
      {editControls && editControls.status === 'guardado' && editControls.savedSecondsAgo !== null && (
        <p className="text-xs text-green-700 mt-0.5">
          {editControls.savedSecondsAgo < 1
            ? 'guardado ahora'
            : `guardado hace ${editControls.savedSecondsAgo} segundo${editControls.savedSecondsAgo === 1 ? '' : 's'}`}
        </p>
      )}
    </div>
  );
};

// ── Status card (edit state only) ───────────────────────────────────────────
const StatusCard = ({ estado, onGoToAnexos }) => {
  if (estado === 'activado') {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-sm font-semibold text-slate-700">Estado de tu perfil</p>
          <EstadoChip estado="activado" />
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">
          Tu perfil está activo desde el {ACTIVATED_AT}. Ya podés generar Anexos.
        </p>
        <button
          type="button"
          onClick={onGoToAnexos}
          className="mt-3 h-11 px-5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-lg inline-flex items-center gap-2 transition-colors"
        >
          Ir a mis Anexos <ArrowRight size={15} />
        </button>
      </div>
    );
  }

  if (estado === 'revisión_requerida') {
    return (
      <div className="bg-white border border-amber-200 rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-sm font-semibold text-slate-700">Estado de tu perfil</p>
          <EstadoChip estado="revisión_requerida" />
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">
          Necesitamos que revises un dato de tu perfil. Detalles abajo.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-sm font-semibold text-slate-700">Estado de tu perfil</p>
        <EstadoChip estado="pendiente_de_activación" />
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">
        Guardamos tu perfil. Estamos activando tu configuración financiera. Te vamos a avisar en
        cuanto esté lista.
      </p>
    </div>
  );
};

// ── Main screen ─────────────────────────────────────────────────────────────
// estado: 'nuevo' | 'pendiente_de_activación' | 'activado' | 'revisión_requerida'
const PerfilAduanero = ({ estado = 'nuevo', onGoToAnexos, onLogout }) => {
  const isNuevo = estado === 'nuevo';

  const [mode, setMode] = useState(isNuevo ? 'setup' : 'edit');
  const [estadoPerfil, setEstadoPerfil] = useState(isNuevo ? 'pendiente_de_activación' : estado);
  const [values, setValues] = useState(isNuevo ? { ...PERFIL_VACIO } : { ...PERFIL_LLENO });
  const [persisted, setPersisted] = useState(isNuevo ? { ...PERFIL_VACIO } : { ...PERFIL_LLENO });
  const [validations, setValidations] = useState({});
  const [saving, setSaving] = useState(false);

  // crossfade + success banner (setup → edit, URL stays /perfil)
  const [faded, setFaded] = useState(false);
  const [banner, setBanner] = useState(false);

  // per-field autosave state (edit mode)
  const [fieldStatus, setFieldStatus] = useState({}); // key → 'guardando' | 'guardado' | 'reintentando'
  const [savedAt, setSavedAt] = useState({}); // key → epoch ms
  const [, setTick] = useState(0);
  const debounceTimers = useRef({});
  const retryTimers = useRef({});
  const timeoutsRef = useRef([]);

  const later = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timeoutsRef.current.push(id);
    return id;
  }, []);

  useEffect(() => {
    const timeouts = timeoutsRef.current;
    const debounces = debounceTimers.current;
    const retries = retryTimers.current;
    return () => {
      timeouts.forEach(clearTimeout);
      Object.values(debounces).forEach(clearTimeout);
      Object.values(retries).forEach(clearTimeout);
    };
  }, []);

  // 1s tick so "guardado hace X segundos" counts up, only while a save is fresh
  const anyFresh = Object.keys(savedAt).length > 0;
  useEffect(() => {
    if (!anyFresh) return undefined;
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [anyFresh]);

  const setFieldValue = (key, raw) => {
    const field = FIELDS.find((f) => f.key === key);
    const next = key === 'codigo_aduana' ? raw.replace(/[^\d]/g, '') : raw;
    setValues((v) => ({ ...v, [key]: next.slice(0, field.maxLength) }));
    // validation waits for blur — never on keystroke
  };

  const runValidation = (key, value) => {
    const result = validateField(key, value);
    setValidations((prev) => ({ ...prev, [key]: result }));
    return result;
  };

  // ── Edit mode: per-field debounced autosave with retry queue ──────────────
  const doFieldSave = useCallback(
    (key, value) => {
      setFieldStatus((s) => ({ ...s, [key]: 'guardando' }));
      mockSave()
        .then(() => {
          setPersisted((p) => ({ ...p, [key]: value }));
          setFieldStatus((s) => ({ ...s, [key]: 'guardado' }));
          setSavedAt((sa) => ({ ...sa, [key]: Date.now() }));
          later(() => {
            setSavedAt((sa) => {
              if (!sa[key] || Date.now() - sa[key] < 3000) return sa;
              const next = { ...sa };
              delete next[key];
              return next;
            });
            setFieldStatus((s) => (s[key] === 'guardado' ? { ...s, [key]: undefined } : s));
          }, 3200);
        })
        .catch(() => {
          // Connection failed: keep the change queued in local state and retry.
          // The value never leaves `values`, so nothing is lost.
          setFieldStatus((s) => ({ ...s, [key]: 'reintentando' }));
          clearTimeout(retryTimers.current[key]);
          retryTimers.current[key] = setTimeout(() => doFieldSave(key, value), 2000);
        });
    },
    [later]
  );

  const scheduleFieldSave = useCallback(
    (key, value) => {
      const result = validateField(key, value);
      if (result.level === 'error' || result.level === 'warn' || !value.trim()) return; // never autosave invalid values
      clearTimeout(debounceTimers.current[key]);
      debounceTimers.current[key] = setTimeout(() => doFieldSave(key, value), 200);
    },
    [doFieldSave]
  );

  const handleBlur = (key) => {
    let value = values[key];
    const field = FIELDS.find((f) => f.key === key);
    if (field.autoUppercase) {
      value = value.toUpperCase();
      setValues((v) => ({ ...v, [key]: value }));
    }
    runValidation(key, value);
    if (mode === 'edit' && value !== persisted[key]) scheduleFieldSave(key, value);
  };

  const handleGuardarCampo = (key) => {
    const value = values[key];
    const result = runValidation(key, value);
    if (result.level === 'error' || result.level === 'warn') return;
    clearTimeout(debounceTimers.current[key]);
    doFieldSave(key, value);
  };

  // ── Setup mode: single primary CTA ────────────────────────────────────────
  const handleSetupSubmit = (e) => {
    e.preventDefault();
    if (saving) return;

    let firstInvalid = null;
    const nextValidations = {};
    FIELDS.forEach((f) => {
      const value = f.autoUppercase ? values[f.key].toUpperCase() : values[f.key];
      let result = validateField(f.key, value);
      if (!value.trim()) result = { level: 'warn', message: 'Completá este campo.' };
      nextValidations[f.key] = result;
      if (!firstInvalid && result.level !== 'ok') firstInvalid = f.key;
    });
    setValidations(nextValidations);

    if (firstInvalid) {
      document.getElementById(firstInvalid)?.focus();
      return;
    }

    setSaving(true);
    mockSave()
      .then(() => {
        setPersisted({ ...values });
        // 200ms crossfade into edit state — same URL, no route change
        setFaded(true);
        later(() => {
          setMode('edit');
          setEstadoPerfil('pendiente_de_activación');
          setValidations({});
          setSaving(false);
          setFaded(false);
          setBanner(true);
          later(() => setBanner(false), 2000);
        }, 200);
      })
      .catch(() => {
        setSaving(false);
      });
  };

  const isSetup = mode === 'setup';

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Top nav */}
      <div className="bg-white border-b border-slate-200 px-5 sm:px-8 h-14 flex items-center justify-between sticky top-0 z-10">
        <SivarSendWordmark size="sm" />
        {onLogout && (
          <button
            type="button"
            onClick={onLogout}
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            Cerrar sesión
          </button>
        )}
      </div>

      {/* Success banner after setup save — visible 2s, auto-dismiss */}
      <div
        aria-live="polite"
        className={`sticky top-14 z-10 transition-opacity duration-200 ${
          banner ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="bg-green-50 border-b border-green-200 px-5 py-3 text-center text-sm text-green-800">
          Perfil guardado. Vamos a activar tu configuración financiera pronto — te avisamos por
          WhatsApp.
        </div>
      </div>

      <div
        className={`max-w-xl mx-auto sm:px-8 py-6 sm:py-10 transition-opacity duration-200 ${
          faded ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {/* Edge-to-edge on mobile, centered card on desktop */}
        <div className="bg-white sm:border sm:border-slate-200 sm:rounded-2xl px-5 py-8 sm:p-10">
          <h1 className="text-xl font-bold text-slate-900">Hola, {CLIENTE.primer_nombre}</h1>
          <p className="text-xs text-slate-400 mt-0.5">{CLIENTE.full_name}</p>

          {isSetup ? (
            <>
              <p className="text-sm text-slate-500 mt-3 leading-relaxed">
                Para generar tu primer Anexo necesitamos tu perfil aduanero. Solo tenés que hacer
                esto una vez.
              </p>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mt-4">
                Paso 1 de 1
              </p>

              <form onSubmit={handleSetupSubmit} className="mt-6 flex flex-col gap-6" noValidate>
                {FIELDS.map((f) => (
                  <CampoPerfil
                    key={f.key}
                    field={f}
                    value={values[f.key]}
                    validation={validations[f.key]}
                    onChange={setFieldValue}
                    onBlur={handleBlur}
                    revisionNote={null}
                    editControls={null}
                  />
                ))}

                <ConfigFinancieraCard estado="pendiente_de_activación" />

                <button
                  type="submit"
                  disabled={saving}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold text-sm rounded-lg transition-colors"
                >
                  {saving ? 'Guardando…' : 'Guardar mi perfil'}
                </button>
              </form>
            </>
          ) : (
            <div className="mt-5 flex flex-col gap-6">
              <StatusCard estado={estadoPerfil} onGoToAnexos={onGoToAnexos} />

              <div className="flex flex-col gap-6">
                {FIELDS.map((f) => (
                  <CampoPerfil
                    key={f.key}
                    field={f}
                    value={values[f.key]}
                    validation={validations[f.key]}
                    onChange={setFieldValue}
                    onBlur={handleBlur}
                    revisionNote={
                      estadoPerfil === 'revisión_requerida' && REVISION.field === f.key
                        ? REVISION.note
                        : null
                    }
                    editControls={{
                      dirty: values[f.key] !== persisted[f.key],
                      status: fieldStatus[f.key],
                      savedSecondsAgo: savedAt[f.key]
                        ? Math.floor((Date.now() - savedAt[f.key]) / 1000)
                        : null,
                      onGuardar: handleGuardarCampo,
                    }}
                  />
                ))}
              </div>

              <ConfigFinancieraCard
                estado={estadoPerfil === 'activado' ? 'activado' : 'pendiente_de_activación'}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerfilAduanero;
